import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoMark } from "./Logo";
import { NAV } from "../data/content";
import { useLang } from "../lib/lang";
import { scrollToId } from "../lib/smooth";

const useNYTime = () => {
  const fmt = () => new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit" }).format(new Date());
  const [t, setT] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setT(fmt()), 15000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-toggle" className="flex rounded-full border border-white/15 p-1 font-mono text-[11px]">
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`relative rounded-full px-3 py-1 uppercase transition-colors ${lang === l ? "text-[var(--ink)]" : "text-zinc-300 hover:text-white"}`}
        >
          {lang === l && <motion.span layoutId="lang-pill" className="absolute inset-0 rounded-full bg-[var(--acid)]" />}
          <span className="relative">{l}</span>
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { tr } = useLang();
  const time = useNYTime();
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-5">
      <nav className="mx-auto flex max-w-7xl items-center justify-between rounded-full border border-white/10 bg-[rgba(18,18,21,0.7)] py-2 pl-2 pr-2 backdrop-blur-xl sm:pr-3">
        <button data-testid="nav-logo" onClick={() => go("top")} className="flex items-center gap-3">
          <LogoMark />
          <span className="font-display text-sm font-bold tracking-tight">eterovic<span className="text-[var(--acid)]">®</span></span>
        </button>
        <div className="hidden items-center gap-6 xl:flex">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n.id)} className="link-u text-sm text-zinc-300 transition-colors hover:text-white">
              {tr(n.label)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="nav-ny-clock" className="hidden font-mono text-[11px] uppercase tracking-widest text-zinc-400 2xl:block">NYC {time}</span>
          <LangToggle />
          <button data-testid="nav-mobile-menu-btn" onClick={() => setOpen((o) => !o)} className="grid h-9 w-9 place-items-center rounded-full bg-white/10 xl:hidden" aria-label="menu">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="mx-auto mt-2 max-w-7xl rounded-3xl border border-white/10 bg-[rgba(18,18,21,0.92)] p-6 backdrop-blur-xl xl:hidden"
          >
            {NAV.map((n, i) => (
              <button key={n.id} data-testid={`mobile-nav-link-${n.id}`} onClick={() => go(n.id)} className="flex w-full items-baseline gap-4 py-3 text-left font-display text-3xl font-bold">
                <span className="font-mono text-xs text-[var(--acid)]">0{i + 1}</span>{tr(n.label)}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
